// Flow inside a block: the products along a shelf read in the order the plan's own CDT ranks
// them, the way the Aesthetics tile reads Flow — neighbours inside a block compared on the
// child level's priority, either direction counting. Each block's stretch on a shelf is
// reordered in place: facings, widths, shelves and the stretch's extent stay as they are.
// The result is a candidate only; the caller judges it on the plan as drawn.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { pogToStudioItems } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import { hierarchyFromColumns } from "@CommonUtils/cdtStudio/domain/pogAesthetics";
import { lanesOf, layoutWith, relayStretch, type Position } from "./gatherBlock";
import { namesNoBlock } from "./plainNames";
import type { GeneratedLayout, GeneratedSegment, StudioItem } from "./studioTypes";

const valueOf = (item: StudioItem | undefined, attr: string): string => {
  const value = item?.attrs[attr]?.trim() ?? "";
  return namesNoBlock(value) ? "—" : value;
};

/** The plan read the way the tile reads it: its CDT columns and their priorities. */
interface GradedRead {
  graded: readonly StudioItem[];
  levels: readonly string[];
  /** The level each level's children are told apart by. */
  childAttrs: readonly string[];
  gradedOf: ReadonlyMap<string, StudioItem>;
}

/** The plan's flow, read once: its graded read, its lanes, and the way each block leans. */
interface FlowRead extends GradedRead {
  lanes: readonly Position[][];
  lean: ReadonlyMap<string, number>;
}

// Plans are never changed in place (a fix stamps a new one), so each is read once: Fix all sorts
// a plan block by block, and every block's sort reads the same plan.
const gradedReads = new WeakMap<Pog, GradedRead>();
const flowReads = new WeakMap<Pog, WeakMap<readonly StudioItem[], FlowRead>>();

const gradedReadOf = (pog: Pog): GradedRead => {
  const known = gradedReads.get(pog);
  if (known) return known;
  const graded = pogToStudioItems([pog]).items;
  const { levels, leafAttr } = hierarchyFromColumns(graded);
  const read = {
    graded,
    levels,
    childAttrs: levels.map((_, depth) => levels[depth + 1] ?? leafAttr ?? ""),
    gradedOf: new Map(graded.map((item) => [item.skuId, item])),
  };
  gradedReads.set(pog, read);
  return read;
};

/** The plan's top-level blocks as the tile reads them, each with the products it holds. */
export const flowBlocksOf = (pog: Pog): Map<string, string[]> => {
  const { graded, levels } = gradedReadOf(pog);
  const [top] = levels;
  const blocks = new Map<string, string[]>();
  if (!top) return blocks;
  for (const item of graded) {
    const block = valueOf(item, top);
    if (block !== "—") blocks.set(block, [...(blocks.get(block) ?? []), item.skuId]);
  }
  return blocks;
};

/** Each product's path down the tile's blocks, a value per level, as the tile names a block
 *  ("Puffs › Plus Lotion"). */
export const tilePathsOf = (pog: Pog): Map<string, string[]> => {
  const { graded, levels } = gradedReadOf(pog);
  return new Map(graded.map((item) => [item.skuId, levels.map((level) => valueOf(item, level))]));
};

const flowReadOf = (pog: Pog, items: readonly StudioItem[]): FlowRead => {
  let byItems = flowReads.get(pog);
  if (!byItems) flowReads.set(pog, (byItems = new WeakMap()));
  const known = byItems.get(items);
  if (known) return known;
  const read = gradedReadOf(pog);
  const lanes = [...lanesOf(items).values()];
  // The way each block leans on its run, counted as the tile counts it: neighbours of two
  // children, compared on the child level's priority.
  const lean = new Map<string, number>();
  for (const lane of lanes) {
    lane.forEach((position, at) => {
      if (at === 0) return;
      const previous = lane[at - 1];
      read.levels.forEach((_, depth) => {
        if (!read.childAttrs[depth]) return;
        if (blockOf(read, previous, depth) !== blockOf(read, position, depth)) return;
        if (childOf(read, previous, depth) === childOf(read, position, depth)) return;
        const a = priorityOf(read, previous, depth);
        const b = priorityOf(read, position, depth);
        if (a === undefined || b === undefined || a === b) return;
        const key = `${position.run}␟${blockOf(read, position, depth)}`;
        lean.set(key, (lean.get(key) ?? 0) + (a < b ? 1 : -1));
      });
    });
  }
  const flow = { ...read, lanes, lean };
  byItems.set(items, flow);
  return flow;
};

const blockOf = (read: GradedRead, position: Position, depth: number) =>
  read.levels
    .slice(0, depth + 1)
    .map((attr) => valueOf(read.gradedOf.get(position.skuId), attr))
    .join("␟");
const childOf = (read: GradedRead, position: Position, depth: number) =>
  valueOf(read.gradedOf.get(position.skuId), read.childAttrs[depth]);
const priorityOf = (read: GradedRead, position: Position, depth: number) =>
  read.gradedOf.get(position.skuId)?.priorities?.[read.childAttrs[depth]];

/**
 * The plan's products in flow order: inside every block, its children by the child level's
 * priority, each block turned the way it already leans on its run so the fewest products move.
 * Only products named in `scope` move, and only among themselves; without a scope, every block.
 * Null when every stretch already reads in order.
 */
export const flowSortedLayout = (
  pog: Pog,
  items: readonly StudioItem[],
  scope?: readonly string[],
): GeneratedLayout | null => {
  if (gradedReadOf(pog).levels.length === 0) return null;
  const read = flowReadOf(pog, items);
  const { levels, childAttrs, lanes, lean } = read;
  const inScope = scope ? new Set(scope) : null;

  // Every stretch that may be reordered: consecutive products of one top-level block.
  const stretches: Position[][] = [];
  for (const lane of lanes) {
    let current: Position[] = [];
    lane.forEach((position, at) => {
      const joins =
        current.length > 0 &&
        (!inScope || inScope.has(position.skuId)) &&
        blockOf(read, position, 0) === blockOf(read, lane[at - 1], 0);
      if (!joins) {
        if (current.length > 1) stretches.push(current);
        current = !inScope || inScope.has(position.skuId) ? [position] : [];
      } else current.push(position);
    });
    if (current.length > 1) stretches.push(current);
  }

  /** A block's stretch with its children in priority order, each child ordered in turn. */
  const ordered = (positions: Position[], depth: number): Position[] => {
    if (depth >= levels.length || !childAttrs[depth]) return positions;
    const children = new Map<string, Position[]>();
    for (const position of positions) {
      const child = childOf(read, position, depth);
      children.set(child, [...(children.get(child) ?? []), position]);
    }
    const descending =
      (lean.get(`${positions[0].run}␟${blockOf(read, positions[0], depth)}`) ?? 0) < 0;
    const rankOf = (group: Position[]) => {
      const priority = priorityOf(read, group[0], depth);
      // A child the plan does not rank keeps to the far end, whichever way the block reads.
      if (priority === undefined) return Infinity;
      return descending ? -priority : priority;
    };
    return [...children.values()]
      .map((group, first) => ({ group, first, rank: rankOf(group) }))
      .sort((a, b) => a.rank - b.rank || a.first - b.first)
      .flatMap(({ group }) => ordered(group, depth + 1));
  };

  const moved = new Map<string, GeneratedSegment>();
  for (const stretch of stretches) relayStretch(stretch, ordered(stretch, 0), moved);
  return moved.size > 0 ? layoutWith(items, moved) : null;
};
